/**
 * FR-140 · Đăng nhập  và  FR-141 · Bước 2FA.
 *
 * Backend trả `401 TWOFA_REQUIRED` kèm `details.challengeId` khi cần bước hai
 * (docs/04 §4.2), nên hai bước nằm chung một màn để giữ challengeId trong bộ nhớ.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ERROR_CODES } from '@dnshub/shared';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { ApiError, api, ensureCsrfCookie } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import type { MeResponse } from '../../lib/types';
import { Field } from '../../components/Field';
import { AuthLayout, FormAlert } from './AuthLayout';

export function LoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const { setSession, refresh } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);

  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);

  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * Công tắc tự đăng ký nằm ở máy chủ (OQ-315). Hỏi một lần khi mở trang.
   *
   * Mặc định `false` rồi mới bật lên nếu máy chủ đồng ý: hỏng đường mạng thì
   * link không hiện, chứ không hiện một lối đi dẫn tới cửa đóng.
   */
  const [choDangKy, setChoDangKy] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const kq = await api.get<{ enabled: boolean }>('/auth/signup');
        if (alive) setChoDangKy(kq.enabled === true);
      } catch {
        /* Im lặng: đây là chi tiết trang trí, không đáng dựng một thông báo lỗi
           chắn ngang người đang muốn đăng nhập. */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function submitCredentials(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await ensureCsrfCookie();
      const me = await api.post<MeResponse>('/auth/login', { email, password, remember });
      setSession(me);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.is(ERROR_CODES.TWOFA_REQUIRED)) {
        setChallengeId(String(err.details?.['challengeId'] ?? ''));
      } else if (err instanceof ApiError) {
        setError(err);
      }
    } finally {
      setBusy(false);
    }
  }

  /*
   * OQ-330 — đăng nhập bằng passkey.
   *
   * Không gửi email lên máy chủ: trình duyệt tự hỏi người dùng chọn khoá nào.
   * Nhờ vậy không có đường nào dò được địa chỉ nào đã có tài khoản.
   *
   * `NotAllowedError` là lúc người dùng bấm Huỷ hoặc để hộp thoại hết giờ — đó
   * KHÔNG phải lỗi, và dựng một thông báo đỏ cho nó sẽ khiến người ta tưởng máy
   * hỏng trong khi chính họ vừa bấm thoát.
   */
  async function submitPasskey() {
    setBusy(true);
    setError(null);
    try {
      await ensureCsrfCookie();
      const { options, luot } = await api.post<{
        options: PublicKeyCredentialRequestOptionsJSON;
        luot: string;
      }>('/auth/passkey/login/begin', {});
      const traLoi = await startAuthentication({ optionsJSON: options });
      const me = await api.post<MeResponse>('/auth/passkey/login/finish', {
        luot,
        traLoi,
        remember,
      });
      setSession(me);
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
        return;
      }
      if (err instanceof ApiError) setError(err);
      else setError(null);
    } finally {
      setBusy(false);
    }
  }

  async function submitTwoFactor(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await api.post<MeResponse>('/auth/2fa/verify', {
        challengeId,
        code: code.trim(),
        trustDevice,
      });
      setSession(me);
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (challengeId !== null) {
    return (
      <AuthLayout
        title={t('auth.2fa.title')}
        lede={t('auth.2fa.lede')}
        foot={
          <button
            className="btn btn-sm btn-ghost"
            type="button"
            onClick={() => {
              setChallengeId(null);
              setCode('');
              setError(null);
            }}
          >
            {t('auth.2fa.backToLogin')}
          </button>
        }
      >
        <form className="stack" onSubmit={submitTwoFactor} data-od-id="form-2fa">
          {error ? <FormAlert>{error.message}</FormAlert> : null}

          <Field
            label={t('auth.2fa.code')}
            hint={t('auth.2fa.codeHint')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="one-time-code"
            inputMode="text"
            autoFocus
            required
            maxLength={32}
            style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.14em' }}
          />

          <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
            <input
              className="check"
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
            />
            <span className="small">{t('auth.2fa.trust')}</span>
          </label>

          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? t('common.processing') : t('auth.2fa.submit')}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.login.title')}
      lede={t('auth.login.lede')}
      foot={
        <>
          <Link className="small" to="/forgot-password">
            {t('auth.login.forgot')}
          </Link>
          {/* Chỉ mời đăng ký khi máy chủ THẬT SỰ đang nhận (OQ-315). Vẽ sẵn rồi
              để người ta bấm vào một biểu mẫu bị từ chối là tệ hơn không có. */}
          {choDangKy ? (
            <Link className="small" to="/register" data-od-id="login-to-register">
              {t('auth.login.toRegister')}
            </Link>
          ) : null}
          <span className="small muted">{t('app.name')}</span>
        </>
      }
    >
      <form className="stack" onSubmit={submitCredentials} data-od-id="form-login">
        {error ? <FormAlert>{error.message}</FormAlert> : null}

        <Field
          label={t('auth.login.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          autoFocus
          required
        />

        <Field
          label={t('auth.login.password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />

        <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
          <input
            className="check"
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <span className="small">{t('auth.login.remember')}</span>
        </label>

        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? t('auth.login.submitting') : t('auth.login.submit')}
        </button>

        {/*
          OQ-330 — chỉ hiện khi trình duyệt thật sự làm được. Một nút bấm vào là
          báo lỗi khó hiểu còn tệ hơn không có nút nào.
        */}
        {browserSupportsWebAuthn() ? (
          <>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--line, #e2e8f0)' }} />
              <span className="small muted">{t('auth.login.or')}</span>
              <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--line, #e2e8f0)' }} />
            </div>
            <button
              className="btn btn-block"
              type="button"
              disabled={busy}
              onClick={() => void submitPasskey()}
              data-od-id="login-passkey"
            >
              {t('auth.login.passkey')}
            </button>
          </>
        ) : null}
      </form>
    </AuthLayout>
  );
}
